import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/api';

const TOKEN_CHECK_INTERVAL = 30000;
const REFRESH_THRESHOLD = 60;

export function decodeToken(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const decoded = JSON.parse(atob(parts[1]));
    return decoded;
  } catch (e) {
    console.error('[useAuthTokens] Erro ao decodificar token:', e);
    return null;
  }
}

export function getTokenExpiration(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  return decoded.exp * 1000;
}

export function isTokenExpired(token) {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true;
  const now = Date.now();
  const timeLeft = expiration - now;
  return timeLeft <= 0;
}

export function isTokenSoonExpiring(token, thresholdSeconds = REFRESH_THRESHOLD) {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true;
  const now = Date.now();
  const timeLeft = expiration - now;
  const thresholdMs = thresholdSeconds * 1000;
  return timeLeft <= thresholdMs;
}

// Autenticação otimista no bootstrap: se já há um access token local e ele
// não está expirado, assumimos autenticado imediatamente (sem tela de
// loading). A validação real acontece em background; se o servidor rejeitar,
// o interceptor do axios redireciona para /login.
function hasValidLocalToken() {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  return !!accessToken && !!refreshToken && !isTokenExpired(accessToken);
}

export function useAuthTokens() {
  const optimisticAuth = hasValidLocalToken();
  const [isAuthenticated, setIsAuthenticated] = useState(optimisticAuth);
  const [user, setUser] = useState(null);
  // Só mostra loading quando NÃO dá para decidir localmente (sem token válido
  // mas com refresh token para tentar renovar). Com token válido → sem loading.
  const [isLoading, setIsLoading] = useState(!optimisticAuth && !!localStorage.getItem('refresh_token'));
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);
  const userRef = useRef(null);
  userRef.current = user;

  const getPushSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return null;
    }

    const registration =
      (await navigator.serviceWorker.getRegistration('/')) ||
      (await navigator.serviceWorker.getRegistration());

    if (!registration) {
      return null;
    }

    return registration.pushManager.getSubscription();
  }, []);

  /**
   * Carrega usuário do endpoint /me (sem dependências para evitar loops)
   */
  const loadUser = useCallback(async () => {
    try {
      const response = await authService.me();
      if (response.data?.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        setError(null);
        return response.data.user;
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      console.error('[useAuthTokens] Erro ao carregar usuário:', err);
      setError(err.message);
      // Só desloga em 401 (token rejeitado pelo backend). Timeout/rede/5xx é
      // transitório: deslogar aqui jogava pro /login quem tinha sessão válida
      // e só estava com conexão lenta. O intervalo periódico tenta de novo.
      if (err?.response?.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
      }
    }
  }, []);

  /**
   * Refresh silencioso usado pelo intervalo periódico.
   * Nunca altera estado React — só atualiza tokens em localStorage.
   * Em caso de falha definitiva, limpa tokens (o próximo ciclo detecta).
   */
  const silentRefresh = useCallback(async () => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) return;

    if (isTokenExpired(accessToken) || isTokenSoonExpiring(accessToken, REFRESH_THRESHOLD)) {
      try {
        await authService.refresh();
      } catch (err) {
        console.error('[useAuthTokens] Silent refresh falhou:', err);
        // Só limpa tokens quando o backend confirma sessão inválida (401).
        // Falha de rede/timeout/5xx é transitória: mantém os tokens para o
        // próximo ciclo (30s) tentar de novo, sem derrubar uma sessão válida
        // dentro da janela de 7 dias.
        if (err?.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
        // Não altera estado React aqui — se os tokens foram removidos, a
        // próxima requisição real vai falhar com 401 e o interceptor do
        // axios redireciona para /login.
      }
    }
  }, []);

  /**
   * Verificação completa de tokens usada apenas na inicialização.
   * Pode atualizar o estado React.
   */
  const checkAndRefreshTokens = useCallback(async () => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
      setIsAuthenticated(false);
      setUser(null);
      return;
    }

    if (isTokenExpired(accessToken)) {
      try {
        await authService.refresh();
        setIsAuthenticated(true);
      } catch (err) {
        console.error('[useAuthTokens] Refresh falhou:', err);
        // Só desloga de verdade em 401 (sessão realmente inválida no backend).
        // Erro transitório (rede/timeout/5xx) mantém o refresh token salvo —
        // o próximo ciclo de silentRefresh tenta de novo em vez de forçar
        // login manual com uma sessão que ainda tem dias de validade.
        if (err?.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setIsAuthenticated(false);
          setUser(null);
        } else {
          // Sessão continua válida no backend: entra e deixa o interceptor
          // (401 → refresh) e o silentRefresh tentarem de novo, em vez de
          // mandar pro /login uma sessão que o próximo F5 restauraria.
          setIsAuthenticated(true);
        }
      }
    } else if (isTokenSoonExpiring(accessToken, REFRESH_THRESHOLD)) {
      try {
        await authService.refresh();
      } catch (err) {
        console.error('[useAuthTokens] Refresh preventivo falhou:', err);
      }
    } else {
      setIsAuthenticated(true);
    }
  }, []);

  /**
   * Inicialização: verifica tokens ao montar
   */
  useEffect(() => {
    const initialize = async () => {
      try {
        await checkAndRefreshTokens();
      } finally {
        // Libera a tela assim que a sessão está decidida. Esperar o /me aqui
        // deixava a LoadingScreen (fundo preto) por até 2× o timeout do axios
        // em conexão lenta. O usuário carrega em background.
        setIsLoading(false);
      }
      const accessToken = localStorage.getItem('access_token');
      if (accessToken && !isTokenExpired(accessToken)) {
        await loadUser();
      }
    };

    initialize();
  }, []);

  /**
   * Setup: Check periódico de tokens
   */
  useEffect(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (isAuthenticated) {
      refreshIntervalRef.current = setInterval(async () => {
        await silentRefresh();
        // /me falhou por erro transitório no bootstrap → tenta de novo.
        if (!userRef.current) loadUser();
      }, TOKEN_CHECK_INTERVAL);

      console.log('[useAuthTokens] Token check interval iniciado');
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [isAuthenticated, silentRefresh, loadUser]);

  /**
   * Login: salva tokens e carrega usuário.
   * ⚠️ NÃO seta o isLoading global: ele é do bootstrap da sessão e o
   * PublicRoute troca a página inteira por "Carregando..." quando ativo —
   * escondia o formulário e engolia as notificações (pior no mobile).
   * O feedback de progresso é o `loading` local de cada página.
   */
  const login = useCallback(async (email, password) => {
    setError(null);

    try {
      const response = await authService.login({ email, password });

      if (response.data?.access_token && response.data?.refresh_token) {
        // Tokens já foram salvos pelo interceptor
        setIsAuthenticated(true);
        // Carrega usuário após login
        await loadUser();
        return response.data;
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Erro ao fazer login';
      setError(message);
      setIsAuthenticated(false);
      setUser(null);
      throw err;
    }
  }, [loadUser]);

  /**
   * Register: cria conta pendente de verificação de email.
   * NÃO autentica — o usuário precisa confirmar o email antes de entrar.
   * Retorna { pending_verification, email }. (Sem isLoading global — ver login.)
   */
  const register = useCallback(async (username, email, password) => {
    setError(null);

    try {
      const response = await authService.register({ username, email, password });
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Erro ao registrar';
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Verify Email: confirma o email via token e já autentica (login automático).
   * (Sem isLoading global — ver login.)
   */
  const verifyEmail = useCallback(async (token) => {
    setError(null);

    try {
      const response = await authService.verifyEmail(token);

      if (response.data?.access_token && response.data?.refresh_token) {
        setIsAuthenticated(true);
        await loadUser();
      }

      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Erro ao confirmar email';
      setError(message);
      throw err;
    }
  }, [loadUser]);

  /**
   * Resend Verification: reenvia o email de confirmação.
   */
  const resendVerification = useCallback(async (email) => {
    setError(null);
    try {
      const response = await authService.resendVerification(email);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Erro ao reenviar email';
      setError(message);
      throw err;
    }
  }, []);

  /**
   * Logout: limpa tokens e dados
   */
  const logout = useCallback(async () => {
    let subscription = null;
    let endpoint = null;

    try {
      subscription = await getPushSubscription();
      endpoint = subscription?.endpoint || null;
    } catch (err) {
      console.warn('[useAuthTokens] Falha ao obter subscription:', err);
    }

    try {
      await authService.logout(endpoint);
    } catch (err) {
      console.error('[useAuthTokens] Erro durante logout:', err);
    } finally {
      if (subscription) {
        try {
          await subscription.unsubscribe();
        } catch (err) {
          console.warn('[useAuthTokens] Falha ao remover subscription local:', err);
        }
      }

      setIsAuthenticated(false);
      setUser(null);
      setError(null);

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    }
  }, []);


  /**
   * Muda senha e faz logout após sucesso
   */
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);

    try {
      const response = await authService.change_password(currentPassword, newPassword);

      setTimeout(() => {
        logout();
      }, 1000);

      return response.data;
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Erro ao alterar senha';
      setError(message);
      throw err;
    }
  }, [logout]);

  /**
   * Forgot Password: envia email de recuperação.
   * (Sem isLoading global — ver login.)
   */
  const forgotPassword = useCallback(async (email) => {
    setError(null);

    try {
      const response = await authService.forgotPassword(email);
      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'Erro ao enviar email de recuperação';

      setError(message);
      throw err;
    }
  }, []);

  /**
   * Reset Password: redefine senha com token.
   * (Sem isLoading global — ver login.)
   */
  const resetPassword = useCallback(async (token, newPassword) => {
    setError(null);

    try {
      const response = await authService.resetPassword(token, newPassword);

      // Se backend retornar novos tokens → já autentica
      if (response.data?.access_token && response.data?.refresh_token) {
        setIsAuthenticated(true);
        await loadUser();
      }

      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'Erro ao redefinir senha';

      setError(message);
      throw err;
    }
  }, [loadUser]);


  return {
    isAuthenticated,
    user,
    isLoading,
    error,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    loadUser,
    checkAndRefreshTokens,
    changePassword,
    forgotPassword,
    resetPassword,
    decodeToken,
    getTokenExpiration,
    isTokenExpired,
    isTokenSoonExpiring,
  };
}

export default useAuthTokens;